import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { PogBBox, ProductRecognitionCropScanResponse } from '../../../types';
import { CropScanItem } from './CropScanItem';

interface CropScansListProps {
  cropScans: ProductRecognitionCropScanResponse[];
  imageUrl: string;
  imageId?: number;
  isFetchingCropScans?: boolean;
  isGeneratingCropScans?: boolean;
  pendingOnly?: boolean;
  showCompleted?: boolean;
  previewSize?: number;
  onCropScanClick?: (cropScan: ProductRecognitionCropScanResponse) => void;
  onSessionEnd?: () => void;
  onInteractiveScanning?: () => void;
  onDelete?: (productCropScanId: number) => void;
  onToggleOrientation?: (productCropScanId: number) => void;
  onSelectDesign?: (productCropScanId: number, designId: string) => void;
  selectedBoxes?: Set<string>;
  pogPreviewsByProductId?: Record<
    number,
    {
      pogBox: PogBBox;
    }
  >;
  emptyDescription?: string;
}

export const CropScansList: React.FC<CropScansListProps> = ({
  cropScans,
  imageUrl,
  isFetchingCropScans,
  isGeneratingCropScans,
  pendingOnly,
  showCompleted,
  previewSize,
  onCropScanClick,
  onDelete,
  onToggleOrientation,
  onSelectDesign,
  selectedBoxes,
  pogPreviewsByProductId,
  emptyDescription = 'No crop scans available for this image.',
}) => {
  // Add pt=0 query parameter for product recognition
  const processedImageUrl = React.useMemo(
    () => imageUrl, //addProductRecognitionQueryParam(imageUrl),
    [imageUrl]
  );

  // Filter to pending-only (assigned but not yet saved) when in review mode
  const displayedCropScans = React.useMemo(() => {
    if (!pendingOnly) return cropScans;
    return cropScans.filter(
      (scan) =>
        (scan as { isPending?: boolean }).isPending ||
        (showCompleted && (scan.barcode || scan.normalized_barcode))
    );
  }, [cropScans, pendingOnly, showCompleted]);

  // Collapse state - default to collapsed
  const [isListCollapsed, setIsListCollapsed] = React.useState(false);

  // Calculate metrics
  const metrics = React.useMemo(() => {
    const totalBoxes = displayedCropScans.length;
    const productBoxes = displayedCropScans.filter(
      (scan) => scan.classification_type === 'product'
    ).length;
    const cartonBoxes = displayedCropScans.filter(
      (scan) => scan.classification_type === 'carton'
    ).length;

    const scannedTotal = displayedCropScans.filter(
      (scan) => scan.barcode || scan.normalized_barcode
    ).length;
    const scannedProducts = displayedCropScans.filter(
      (scan) => scan.classification_type === 'product' && (scan.barcode || scan.normalized_barcode)
    ).length;
    const scannedCartons = displayedCropScans.filter(
      (scan) => scan.classification_type === 'carton' && (scan.barcode || scan.normalized_barcode)
    ).length;

    const totalPercent = totalBoxes > 0 ? Math.round((scannedTotal / totalBoxes) * 100) : 0;
    const productPercent =
      productBoxes > 0 ? Math.round((scannedProducts / productBoxes) * 100) : 0;
    const cartonPercent = cartonBoxes > 0 ? Math.round((scannedCartons / cartonBoxes) * 100) : 0;

    return {
      all: { scanned: scannedTotal, total: totalBoxes, percent: totalPercent },
      products: {
        scanned: scannedProducts,
        total: productBoxes,
        percent: productPercent,
      },
      cartons: {
        scanned: scannedCartons,
        total: cartonBoxes,
        percent: cartonPercent,
      },
    };
  }, [displayedCropScans]);

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      {/* Metrics and Collapse Toggle */}
      {!isFetchingCropScans && !isGeneratingCropScans && displayedCropScans.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={`${'All'}: ${metrics.all.scanned}/${
                metrics.all.total
              } (${metrics.all.percent}%)`}
              color="primary"
              variant="outlined"
              size="small"
            />
            {metrics.products.total > 0 && (
              <Chip
                label={`${'Products'}: ${metrics.products.scanned}/${
                  metrics.products.total
                } (${metrics.products.percent}%)`}
                sx={{
                  backgroundColor: '#E8F5E8',
                  color: '#2E7D32',
                  '& .MuiChip-label': { fontWeight: 'bold' },
                }}
                size="small"
              />
            )}
            {metrics.cartons.total > 0 && (
              <Chip
                label={`${'Cartons'}: ${metrics.cartons.scanned}/${
                  metrics.cartons.total
                } (${metrics.cartons.percent}%)`}
                sx={{
                  backgroundColor: '#FFF3E0',
                  color: '#F57C00',
                  '& .MuiChip-label': { fontWeight: 'bold' },
                }}
                size="small"
              />
            )}
          </Stack>

          <IconButton onClick={() => setIsListCollapsed(!isListCollapsed)} size="small">
            {isListCollapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Stack>
      )}

      {isFetchingCropScans ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'center',
            py: 4,
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      ) : isGeneratingCropScans ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'center',
            py: 4,
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Generating crop scans...
          </Typography>
        </Box>
      ) : displayedCropScans.length === 0 ? (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {emptyDescription}
          </Typography>
        </Box>
      ) : (
        <Collapse in={!isListCollapsed} timeout="auto" unmountOnExit>
          <Stack gap={1}>
            {displayedCropScans.map((cropScan, index) => {
              const isSelected = selectedBoxes?.has(cropScan.product_crop_scan_id.toString());
              const pogPreview =
                cropScan.product_id && pogPreviewsByProductId
                  ? pogPreviewsByProductId[cropScan.product_id]
                  : undefined;
              return (
                <CropScanItem
                  key={cropScan.product_crop_scan_id}
                  cropScan={{
                    ...cropScan,
                    pogPreview,
                  }}
                  index={index}
                  imageUrl={processedImageUrl}
                  previewSize={previewSize}
                  onCropScanClick={onCropScanClick}
                  onDelete={onDelete}
                  onToggleOrientation={onToggleOrientation}
                  onSelectDesign={onSelectDesign}
                  isSelected={isSelected}
                />
              );
            })}
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};
